<template>
  <div>
    <!-- Exclude Sources -->
    <div class="mb-4">
      <v-checkbox
        v-model="excludeSourcesEnabled"
        label="Исключить выбранные источники"
        density="compact"
        hide-details
        class="mb-2"
      ></v-checkbox>

      <v-expand-transition>
        <div v-if="excludeSourcesEnabled">
          <v-autocomplete
            v-model="selectedSources"
            :items="sourcesItemsMerged"
            :loading="sourcesLoading"
            :search="sourcesSearch"
            @update:search="searchSources"
            @update:model-value="sourcesSearch = ''"
            label="Выберите источники для исключения"
            hint="Начните вводить название источника"
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="name"
            item-value="id"
            return-object
            clearable
            no-filter
            class="ml-8"
            :chip-props="{ size: 'small', color: 'error' }"
            :menu-props="{ location: 'bottom', maxHeight: 400 }"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item
                v-bind="props"
                :title="item.raw.name"
                :subtitle="getSourceSubtitle(item.raw)"
              >
                <template v-slot:prepend>
                  <v-icon :icon="getSourceIcon(item.raw)" size="small"></v-icon>
                </template>
              </v-list-item>
            </template>

            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  Начните вводить для поиска источников
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </div>
      </v-expand-transition>
    </div>

    <!--
      TODO: Exclude Authors — hidden until full implementation is ready.
      Backend stores exclude_author_ids but filtering is not yet applied
      in data/tasks services. See Task Master for the task.
    -->

    <!-- Help Text -->
    <v-alert
      type="info"
      variant="tonal"
      density="compact"
    >
      <template v-slot:text>
        <strong>Исключения:</strong> Сообщения из выбранных источников
        не будут учитываться в результатах мониторинга.
      </template>
    </v-alert>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import apiClient from '@/apiClient';

// Props and emits
const props = defineProps({
  sources: {
    type: Array,
    default: () => [],
  },
  authors: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:sources', 'update:authors']);

// Refs for sources
const excludeSourcesEnabled = ref(false);
const selectedSources = ref([]);
const sourcesList = ref([]);
const sourcesLoading = ref(false);
const sourcesSearch = ref('');

// Merged items: always include selected items so chips render correctly
const sourcesItemsMerged = computed(() => {
  const selectedIds = new Set(selectedSources.value.map(s => typeof s === 'object' ? s.id : s));
  const merged = [...selectedSources.value.filter(s => typeof s === 'object')];
  for (const item of sourcesList.value) {
    if (!selectedIds.has(item.id)) {
      merged.push(item);
    }
  }
  return merged;
});

// Refs for authors
const excludeAuthorsEnabled = ref(false);
const selectedAuthors = ref([]);
const authorsList = ref([]);
const authorsLoading = ref(false);
const authorsSearch = ref('');

// Merged items for authors
const authorsItemsMerged = computed(() => {
  const selectedIds = new Set(selectedAuthors.value.map(a => typeof a === 'object' ? a.id : a));
  const merged = [...selectedAuthors.value.filter(a => typeof a === 'object')];
  for (const item of authorsList.value) {
    if (!selectedIds.has(item.id)) {
      merged.push(item);
    }
  }
  return merged;
});

// Helper to extract IDs for comparison
const toIds = (arr) => (arr || []).map(item => typeof item === 'object' ? item.id : item);

// Watch for prop changes (only update if different to avoid loops)
watch(
  () => props.sources,
  (newValue) => {
    if (!newValue) return;
    const newIds = toIds(newValue);
    const currentIds = toIds(selectedSources.value);
    // Only overwrite if IDs actually differ (avoids replacing objects with plain IDs)
    if (JSON.stringify(newIds) !== JSON.stringify(currentIds)) {
      selectedSources.value = newValue || [];
      excludeSourcesEnabled.value = selectedSources.value.length > 0;
    }
  },
  { immediate: true }
);

watch(
  () => props.authors,
  (newValue) => {
    if (!newValue) return;
    const newIds = toIds(newValue);
    const currentIds = toIds(selectedAuthors.value);
    if (JSON.stringify(newIds) !== JSON.stringify(currentIds)) {
      selectedAuthors.value = newValue || [];
      excludeAuthorsEnabled.value = selectedAuthors.value.length > 0;
    }
  },
  { immediate: true }
);

// Watch for selection changes (only emit if different to avoid loops)
let isUpdatingSources = false;
watch(selectedSources, (newValue) => {
  if (isUpdatingSources) return;

  // Extract only IDs from the objects
  const emitValue = excludeSourcesEnabled.value
    ? (newValue || []).map(item => typeof item === 'object' ? item.id : item)
    : [];

  if (JSON.stringify(emitValue) !== JSON.stringify(props.sources)) {
    isUpdatingSources = true;
    emit('update:sources', emitValue);
    setTimeout(() => { isUpdatingSources = false; }, 0);
  }
});

let isUpdatingAuthors = false;
watch(selectedAuthors, (newValue) => {
  if (isUpdatingAuthors) return;

  // Extract only IDs from the objects
  const emitValue = excludeAuthorsEnabled.value
    ? (newValue || []).map(item => typeof item === 'object' ? item.id : item)
    : [];

  if (JSON.stringify(emitValue) !== JSON.stringify(props.authors)) {
    isUpdatingAuthors = true;
    emit('update:authors', emitValue);
    setTimeout(() => { isUpdatingAuthors = false; }, 0);
  }
});

// Watch for enable/disable
watch(excludeSourcesEnabled, (enabled) => {
  if (!enabled) {
    selectedSources.value = [];
    emit('update:sources', []);
  }
});

watch(excludeAuthorsEnabled, (enabled) => {
  if (!enabled) {
    selectedAuthors.value = [];
    emit('update:authors', []);
  }
});

// Methods for sources
const searchSources = async (query) => {
  sourcesSearch.value = query;
  if (!query || query.length < 2) {
    sourcesList.value = [];
    return;
  }

  sourcesLoading.value = true;

  try {
    const response = await apiClient.get('/ref/sources', {
      params: {
        search: query,
        limit: 50,
      },
    });
    sourcesList.value = response.data.items || [];
  } catch (error) {
    console.error('Error searching sources:', error);
    sourcesList.value = [];
  } finally {
    sourcesLoading.value = false;
  }
};

const getSourceIcon = (source) => {
  if (source.type === 'platform') {
    return 'mdi-share-variant';
  } else if (source.type === 'media') {
    return 'mdi-newspaper';
  }
  return 'mdi-web';
};

const getSourceSubtitle = (source) => {
  const parts = [];
  if (source.platform_name) {
    parts.push(source.platform_name);
  }
  if (source.media_kind_name) {
    parts.push(source.media_kind_name);
  }
  if (source.level) {
    parts.push(source.level);
  }
  return parts.join(' • ') || source.url;
};

// Methods for authors
const searchAuthors = async (query) => {
  authorsSearch.value = query;
  if (!query || query.length < 2) {
    authorsList.value = [];
    return;
  }

  authorsLoading.value = true;

  try {
    const response = await apiClient.get('/ref/authors', {
      params: {
        search: query,
        limit: 50,
      },
    });
    authorsList.value = response.data.items || [];
  } catch (error) {
    console.error('Error searching authors:', error);
    authorsList.value = [];
  } finally {
    authorsLoading.value = false;
  }
};
</script>

<style scoped>
/* Component-specific styles */
</style>
